<template>
  <v-menu v-model="isShown" :offset="[0, 8]" location="bottom end" class="profile-dropdown">
    <template #activator="{ props: menuProps }">
      <v-btn
        v-bind="menuProps"
        variant="text"
        density="comfortable"
        class="navbar-icon-btn profile-dropdown__button"
        aria-label="Account menu"
      >
        <v-avatar :size="32" color="warning">
          <v-icon icon="person" size="20" />
        </v-avatar>
      </v-btn>
    </template>
    <v-card class="profile-dropdown__content md:w-60 px-0 py-4 w-full" :style="{ '--hover-color': hoverColor }">
      <div v-for="group in options" :key="group.name">
        <header v-if="group.name" class="uppercase text-text-secondary opacity-80 font-bold text-xs px-4">
          {{ t(`user.${group.name}`) }}
        </header>
        <v-list density="compact">
          <v-list-item v-for="item in group.list" :key="item.name" class="menu-item" @click="handleItemClick(item)">
            <template #prepend>
              <v-icon :icon="item.icon" color="secondary" />
            </template>
            <v-list-item-title>{{ t(`user.${item.name}`) }}</v-list-item-title>
          </v-list-item>
        </v-list>
        <v-divider v-if="group.separator" class="mx-3 my-2" />
      </div>
    </v-card>
  </v-menu>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useColors } from '@/composables/ui'
import { authService } from '../../../../services/auth'
import { useAuthStore } from '../../../../stores/auth-store'
import { IS_FREE_ACCESS_MODE } from '../../../../config/freeAccess'

const { getColor, colorToRgba } = useColors()
const hoverColor = computed(() => colorToRgba(getColor('focus'), 0.1))

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
  action?: () => void
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

const isUserLoggedIn = computed(() => authService.isUserToken())
const isAuthenticated = computed(() => authService.isAuthenticated())

const defaultOptions: ProfileOptions[] = [
  {
    name: 'account',
    separator: true,
    list: [
      { name: 'createAccount', to: 'shop-account-setup', icon: 'person_add' },
      { name: 'profile', to: 'preferences', icon: 'account_circle' },
      ...(IS_FREE_ACCESS_MODE ? [] : [{ name: 'pricing', to: 'pricing-plans', icon: 'receipt_long' }]),
    ],
  },
  {
    name: 'explore',
    separator: true,
    list: [{ name: 'helpAndSupport', href: 'https://netlyze.io/support', icon: 'support_agent' }],
  },
  {
    name: '',
    separator: false,
    list: [
      {
        name: 'logout',
        icon: 'logout',
        action: async () => {
          await authStore.logout()
          router.push({ name: 'login' })
        },
      },
    ],
  },
]

const userMenuOptions: ProfileOptions[] = [
  {
    name: 'account',
    separator: true,
    list: [
      { name: 'profile', to: 'user-profile', icon: 'account_circle' },
      { name: 'manageStores', to: 'store-management', icon: 'store' },
      { name: 'changePassword', to: 'change-password', icon: 'lock' },
    ],
  },
  {
    name: 'explore',
    separator: true,
    list: [{ name: 'helpAndSupport', href: 'https://netlyze.io/support', icon: 'support_agent' }],
  },
  {
    name: '',
    separator: false,
    list: [
      {
        name: 'logout',
        icon: 'logout',
        action: async () => {
          await authStore.logout()
          router.push({ name: 'login' })
        },
      },
    ],
  },
]

const options = computed(() => {
  if (isUserLoggedIn.value) return userMenuOptions
  if (!isAuthenticated.value) {
    return defaultOptions.map((group) => {
      if (group.name === 'account') {
        return { ...group, list: group.list.filter((item) => item.name !== 'createAccount') }
      }
      return group
    })
  }
  return defaultOptions
})

const isShown = ref(false)

const handleItemClick = (item: ProfileListItem) => {
  if (item.action) {
    item.action()
    isShown.value = false
  } else if (item.to) {
    router.push({ name: item.to }).catch(() => undefined)
    isShown.value = false
  } else if (item.href) {
    window.open(item.href, '_blank')
    isShown.value = false
  }
}
</script>

<style lang="scss" scoped>
.profile-dropdown {
  &__button {
    width: 40px;
    height: 40px;
  }

  &__content .menu-item:hover {
    background: var(--hover-color);
  }
}
</style>
